import APP from '$/api/api';
import { canvasRequest } from '$/api/canvasRquest';
import {
  getMyIpResourceKey, isMyIpResourceActive, MY_IP_RESOURCE_TYPES, omitMyIpProfileMaterials, preserveMyIpLegacyMedia,
} from './resourceSlots';
import { getMyIpMediaUrl } from './mediaUrl';
import { getMyIpMaterialKeys, isMyIpMaterialRemoved } from './materialCollection';
import { getMyIpEditDraft, mergeMyIpEditProfile, validateMyIpEdit } from './editProfile';
import type { MyIpEditDraft } from './editProfile';
import { enqueueMyIpWrite } from './writeQueue';
import { rebaseMyIpSnapshot } from './rebaseSnapshot';
import { isMyIpVersionConflict } from './versionConflict';
import { notifyMyIpChanged } from './changeEvents';
import { validateMyIpRoomId } from './agentBinding';
import { MY_IP_MAX_MATERIALS } from './materialLimits';
import type {
  MyIpCreationResult, MyIpDraftSnapshot, MyIpItem, MyIpList, MyIpPage, MyIpPageParams,
  MyIpProfile, MyIpProject, MyIpProjectPage, MyIpResource, MyIpSummary,
} from './types';

/** 业务错误与协议错误保留状态，页面可区分无权、不存在和可重试失败。 */
export class MyIpRequestError extends Error {
  readonly code?: number | string;

  readonly status?: number;

  /** 错误消息仅用于诊断，可见提示由调用方使用 Smartcat 文案。 */
  constructor(message: string, code?: number | string, status?: number) {
    super(message);
    this.name = 'MyIpRequestError';
    this.code = code;
    this.status = status;
  }
}

/** 只按后端独立错误码识别画布 IP 数量上限，兼容业务响应和非 2xx 响应。 */
export function isMyIpCanvasLimitError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const value = error as { code?: number | string; response?: { data?: { code?: number | string; }; }; };
  return Number(value.code ?? value.response?.data?.code) === 15626;
}

/** 拒绝异常负载，不能把缺失统计伪装成零或无访问资格。 */
function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new MyIpRequestError('Invalid IP response object');
  }
  return value as Record<string, unknown>;
}

/** 可选文本缺省为空，不把对象或资产 ID 推断成可访问 URL。 */
function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** 仅展示服务端明确给出的 HTTP(S) 封面，不拼接资产 ID 或执行非媒体协议。 */
function coverUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? value : undefined;
  } catch {
    return undefined;
  }
}

/** uint64 全程以字符串传递；拒绝已损失精度的 JS number。 */
export function normalizeIpId(value: unknown): string {
  const id = typeof value === 'number' && Number.isSafeInteger(value) ? String(value) : text(value);
  if (!/^[1-9]\d*$/.test(id) || id.length > 20
    || (id.length === 20 && id > '18446744073709551615')) {
    throw new MyIpRequestError('Invalid IP identifier');
  }
  return id;
}

/** Go uint64 请求字段需要 JSON 数字；只在序列化时输出已校验的十进制字面量。 */
export function serializeMyIpCreation(value: unknown): string {
  const data = record(value);
  const id = normalizeIpId(data.ip_id);
  // uint64 仅在目标协议字段输出数字字面量，profile 中同名键保持原样。
  return `{${Object.entries({ ip_id: id, ...data }).map(([key, entry]) => {
    if (key === 'ip_id') return `"ip_id":${id}`;
    if (key === 'resources' && Array.isArray(entry)) {
      const resources = entry.map((resource) => {
        const row = record(resource);
        return `{${Object.entries(row).filter(([, field]) => field !== undefined).map(([field, fieldValue]) => (
          `${JSON.stringify(field)}:${field === 'id' ? normalizeIpId(fieldValue) : JSON.stringify(fieldValue)}`
        )).join(',')}}`;
      });
      return `"resources":[${resources.join(',')}]`;
    }
    return entry === undefined ? undefined : `${JSON.stringify(key)}:${JSON.stringify(entry)}`;
  }).filter(Boolean).join(',')}}`;
}

/** 批量绑定的 uint64 数组仅在最终 JSON 序列化时转换为数字字面量。 */
export function serializeMyIpProjectBinding(value: unknown): string {
  const data = record(value);
  if (!Array.isArray(data.ip_ids) || data.ip_ids.length < 1 || data.ip_ids.length > 100) {
    throw new MyIpRequestError('Invalid IP project binding list');
  }
  const ipIds = data.ip_ids.map(normalizeIpId);
  const serializedIpIds = JSON.stringify(ipIds);
  return JSON.stringify({ ...data, ip_ids: ipIds })
    .replace(`"ip_ids":${serializedIpIds}`, `"ip_ids":[${ipIds.join(',')}]`);
}

/** 项目、房间及资产标识为后端字符串，不对其格式作数字假设。 */
function requiredString(value: unknown): string {
  const result = text(value);
  if (!result) throw new MyIpRequestError('Missing IP response identifier');
  return result;
}

/** 计数必须真实存在且无损，异常值交给错误重试分支。 */
function count(value: unknown): number {
  // catalyze 的无损 JSON 解析器会将长整数转成字符串，安全整数范围内可还原展示。
  const result = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  if (typeof result !== 'number' || !Number.isSafeInteger(result) || result < 0) {
    throw new MyIpRequestError('Invalid IP response count');
  }
  return result;
}

/** 服务端允许未初始化数组为 null，其他异常类型不静默吞掉。 */
function array(value: unknown): unknown[] {
  if (value == null) return [];
  if (!Array.isArray(value)) throw new MyIpRequestError('Invalid IP response list');
  return value;
}

/** 资格只能取服务端布尔值，不由 total 或前端列表长度推算。 */
function pageVisible(value: unknown): boolean {
  if (typeof value !== 'boolean') throw new MyIpRequestError('Missing IP eligibility');
  return value;
}

/** WebAPI 信封只在 HTTP 和业务状态均成功时解包，列表接口可按需接受成功空数据。 */
function unwrap(response: { status: number; data: unknown; }, allowEmptyData = false): Record<string, unknown> {
  const envelope = record(response.data);
  if (response.status !== 200 || envelope.code !== 0) {
    throw new MyIpRequestError(
      text(envelope.message) || 'IP request failed',
      typeof envelope.code === 'number' || typeof envelope.code === 'string' ? envelope.code : undefined,
      response.status,
    );
  }
  if (allowEmptyData && (envelope.data == null
    || (Array.isArray(envelope.data) && envelope.data.length === 0))) return {};
  return record(envelope.data);
}

/** 元数据未知时保持缺省；只接受非负有限数值，时长保留秒的小数。 */
function mediaNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}

/** 详情保留资源元信息；兼容历史 URL 型 asset_id，优先使用当前响应的展示地址。 */
function normalizeMyIpResource(value: unknown): MyIpResource {
  const resource = record(value);
  return {
    id: normalizeIpId(resource.id),
    assetId: typeof resource.asset_id === 'number' ? normalizeIpId(resource.asset_id) : text(resource.asset_id),
    type: text(resource.resource_type),
    role: text(resource.resource_role),
    sortOrder: typeof resource.sort_order === 'number' ? resource.sort_order : 0,
    status: text(resource.status),
    ...(typeof resource.available === 'boolean' ? { available: resource.available } : {}),
    ...(typeof resource.name === 'string' ? { name: resource.name } : {}),
    ...(typeof resource.voice_id === 'string' || (typeof resource.voice_id === 'number' && Number.isSafeInteger(resource.voice_id)) ? { voiceId: String(resource.voice_id) } : {}),
    ...(coverUrl(resource.url) || coverUrl(resource.media_url) || coverUrl(resource.asset_id)
      ? { url: coverUrl(resource.url) || coverUrl(resource.media_url) || coverUrl(resource.asset_id) } : {}),
    ...(coverUrl(resource.thumbnail_url) || coverUrl(resource.media_cover_url)
      ? { thumbnailUrl: coverUrl(resource.thumbnail_url) || coverUrl(resource.media_cover_url) } : {}),
    ...(coverUrl(resource.watermark_url) || coverUrl(resource.media_url_by_water_mark)
      ? { watermarkUrl: coverUrl(resource.watermark_url) || coverUrl(resource.media_url_by_water_mark) } : {}),
    ...(coverUrl(resource.watermark_cover_url) || coverUrl(resource.media_cover_url_by_water_mark)
      ? { watermarkCoverUrl: coverUrl(resource.watermark_cover_url) || coverUrl(resource.media_cover_url_by_water_mark) } : {}),
    ...(typeof resource.file_extension === 'string' ? { fileExtension: resource.file_extension } : {}),
    ...(mediaNumber(resource.duration) === undefined ? {} : { duration: mediaNumber(resource.duration) }),
    ...(mediaNumber(resource.file_size) === undefined ? {} : { fileSize: mediaNumber(resource.file_size) }),
    ...(mediaNumber(resource.width) === undefined ? {} : { width: mediaNumber(resource.width) }),
    ...(mediaNumber(resource.height) === undefined ? {} : { height: mediaNumber(resource.height) }),
  };
}

/** 兼容旧三视图为待入库资源；正式槽位（含撤回）和删除记录优先，读取不触发写入。 */
export function normalizeMyIp(value: unknown): MyIpItem {
  const data = record(value);
  const item: MyIpItem = {
    ipId: normalizeIpId(data.ip_id),
    // 账号 UID 在应用态为字符串；接口可能以安全整数返回，统一转成字符串供展示和埋点使用。
    ...(typeof data.creator_id === 'string'
      || (typeof data.creator_id === 'number' && Number.isSafeInteger(data.creator_id))
      ? { creatorId: String(data.creator_id) }
      : {}),
    ...(data.source_session_id !== undefined ? { sourceSessionId: text(data.source_session_id) } : {}),
    name: text(data.name),
    summary: text(data.summary),
    coverAssetId: typeof data.cover_asset_id === 'number' ? normalizeIpId(data.cover_asset_id) : text(data.cover_asset_id),
    coverUrl: coverUrl(data.cover_url),
    coverWatermarkUrl: coverUrl(data.cover_watermark_url),
    avatarUrl: coverUrl(data.avatar_url),
    avatarWatermarkUrl: coverUrl(data.avatar_watermark_url),
    assetType: text(data.asset_type),
    status: text(data.status),
    version: count(data.version),
    createdAt: count(data.created_at),
    referenceCount: count(data.reference_count),
    profile: data.profile == null ? {} : omitMyIpProfileMaterials(record(data.profile)),
    resources: array(data.resources).filter((resource) => resource != null).map(normalizeMyIpResource),
  };
  const legacyThreeView = getMyIpMediaUrl(item.profile.turnaround_visual);
  if (legacyThreeView && !item.resources.some((resource) => resource.role === 'three_view')
    && !isMyIpMaterialRemoved(item.profile, { url: legacyThreeView })) {
    item.resources.push({
      id: '', assetId: '', role: 'three_view', type: 'image', url: legacyThreeView, status: 'active', sortOrder: 0,
    });
  }
  return item;
}

/** 页码遵循 API 上限，避免无效分页造成列表重复追加。 */
function pagination({ page = 1, pageSize = 20 }: MyIpPageParams) {
  if (!Number.isSafeInteger(page) || page < 1
    || !Number.isSafeInteger(pageSize) || pageSize < 1 || pageSize > 100) {
    throw new MyIpRequestError('Invalid IP pagination');
  }
  return { page, page_size: pageSize };
}

/** mine 是当前业务空间的个人资产类型，必须保留公共参数及 Client-OID。 */
export async function getMyIpSummary(signal?: AbortSignal): Promise<MyIpSummary> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpSummaryV3, method: 'GET', signal,
  }));
  return {
    ipCount: count(data.total),
    cumulativeProjectCount: count(data.project_total),
    pageVisible: pageVisible(data.page_visible),
  };
}

/** 原样保留后端跨页顺序，仅请求当前空间已完成的自有 IP。 */
export async function getMyIpList(params: MyIpPageParams = {}): Promise<MyIpList> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpListV3,
    method: 'GET',
    params: {
      scene: 'mine', asset_type: 'personal', status: 'complete', ...pagination(params),
    },
    signal: params.signal,
  }));
  const list = array(data.list).map(normalizeMyIp);
  if (list.some((item) => item.assetType !== 'personal' || item.status !== 'complete')) {
    throw new MyIpRequestError('Unexpected item in completed personal IP list');
  }
  return { list, total: count(data.total), pageVisible: pageVisible(data.page_visible) };
}

export interface MyIpAssetListParams extends MyIpPageParams {
  assetType: 'personal' | 'official';
  keyword?: string;
}

/** 资产库不受「我的 IP」页面资格门控；检索交给服务端覆盖名称和检索文案。 */
export async function getMyIpAssetList(params: MyIpAssetListParams): Promise<MyIpPage<MyIpItem>> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpListV3,
    method: 'GET',
    params: {
      scene: 'asset_lib',
      asset_type: params.assetType,
      status: 'complete',
      keyword: params.keyword?.trim() || undefined,
      ...pagination(params),
    },
    signal: params.signal,
  }));
  const list = array(data.list).map(normalizeMyIp);
  if (list.some((item) => item.assetType !== params.assetType || item.status !== 'complete')) {
    throw new MyIpRequestError('Unexpected item in IP asset library');
  }
  return { list, total: count(data.total) };
}

export interface MyIpPickerListParams extends MyIpPageParams {
  assetType: 'personal' | 'official';
}

/** Home 输入框角色选择使用 picker 场景。 */
export async function getMyIpPickerList(params: MyIpPickerListParams): Promise<MyIpPage<MyIpItem>> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpListV3,
    method: 'GET',
    params: {
      scene: 'picker',
      asset_type: params.assetType,
      status: 'complete',
      ...pagination(params),
    },
    signal: params.signal,
  }));
  const list = array(data.list).map(normalizeMyIp);
  if (list.some((item) => item.assetType !== params.assetType || item.status !== 'complete')) {
    throw new MyIpRequestError('Unexpected item in IP picker');
  }
  return { list, total: count(data.total) };
}

export interface MyIpProjectListParams extends MyIpPageParams {
  projectId: string;
  roomId: string;
}

/** 按项目和房间读取引用，不发送资产分类；ProjectIPVO.ip 无分页，数量适配列表 Hook。 */
export async function getMyIpProjectList(params: MyIpProjectListParams): Promise<MyIpPage<MyIpItem>> {
  const projectId = params.projectId.trim();
  const roomId = params.roomId.trim();
  if (!projectId || !roomId) throw new MyIpRequestError('Missing IP project or room identifier');
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpProjectListV3,
    method: 'GET',
    params: { project_id: projectId, room_id: roomId },
    signal: params.signal,
  }), true);
  const list = array(data.list).flatMap((value) => {
    const binding = record(value);
    // 兼容历史直接返回主档的部署；新响应必须从引用的 ip 字段读取展示信息。
    if (!Object.prototype.hasOwnProperty.call(binding, 'ip')) return [normalizeMyIp(binding)];
    if (binding.status !== 'active' || binding.ip == null) return [];
    const item = normalizeMyIp(binding.ip);
    if (normalizeIpId(binding.ip_id) !== item.ipId || binding.project_id !== projectId) {
      throw new MyIpRequestError('Unexpected IP project binding');
    }
    return [item];
  });
  if (list.some((item) => !['personal', 'official'].includes(item.assetType) || item.status !== 'complete')) {
    throw new MyIpRequestError('Unexpected item in project IP picker');
  }
  return { list, total: list.length };
}

/** URL 单独重建有权限详情；官方资产共用展示页，但编辑仍由独立权限约束保护。 */
export async function getMyIpDetail(ipId: string, signal?: AbortSignal): Promise<MyIpItem> {
  const id = normalizeIpId(ipId);
  const item = normalizeMyIp(unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpDetailV3, method: 'GET', params: { ip_id: id }, signal,
  })));
  if (item.ipId !== id || !['personal', 'official'].includes(item.assetType) || item.status !== 'complete') {
    throw new MyIpRequestError('IP is unavailable on personal page', undefined, 404);
  }
  return item;
}

/** 主字段默认不传版本；旧服务兼容重试才使用刚刷新详情的版本。 */
async function requestMyIpUpdate(item: MyIpItem, draft: MyIpEditDraft, locale: string, signal?: AbortSignal, includeVersion = false): Promise<MyIpItem> {
  if (item.assetType !== 'personal' || item.status !== 'complete' || validateMyIpEdit(item, draft, locale).length) {
    throw new MyIpRequestError('Invalid IP edit');
  }
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpUpdateV3,
    method: 'POST',
    data: {
      ip_id: normalizeIpId(item.ipId),
      ...(includeVersion ? { version: item.version } : {}),
      name: draft.name,
      summary: draft.summary,
      profile: omitMyIpProfileMaterials(mergeMyIpEditProfile(item, draft)),
    },
    signal,
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpCreation],
  }));
  record(data.profile);
  const updated = normalizeMyIp(data);
  if (updated.ipId !== item.ipId || updated.version <= item.version
    || updated.assetType !== 'personal' || updated.status !== 'complete') {
    throw new MyIpRequestError('Unexpected IP update result');
  }
  // 本次仅编辑文本，update 允许不组装 resources，不能因此清掉详情中的现有媒体。
  if (updated.sourceSessionId !== undefined && updated.sourceSessionId !== item.sourceSessionId) {
    throw new MyIpRequestError('Unexpected IP source session');
  }
  notifyMyIpChanged('content');
  return {
    ...updated,
    sourceSessionId: updated.sourceSessionId ?? item.sourceSessionId,
    // 部分 update 响应只返回变更字段，未返回头像时保留详情已授权的地址。
    avatarUrl: Object.hasOwn(data, 'avatar_url') ? updated.avatarUrl : item.avatarUrl,
    coverWatermarkUrl: Object.hasOwn(data, 'cover_watermark_url') ? updated.coverWatermarkUrl : item.coverWatermarkUrl,
    avatarWatermarkUrl: Object.hasOwn(data, 'avatar_watermark_url') ? updated.avatarWatermarkUrl : item.avatarWatermarkUrl,
    resources: data.resources == null ? item.resources : updated.resources,
  };
}

/** 只保留具备有效身份、创建信息和可导航房间类型的关联项目。 */
function normalizeMyIpProject(value: unknown): MyIpProject | undefined {
  const item = record(value);
  const projectId = text(item.project_id);
  const roomId = text(item.room_id);
  const roomType = text(item.room_type);
  const name = text(item.name) || text(item.room_title);
  const creatorIdValid = (typeof item.created_by_uid === 'number'
    && Number.isSafeInteger(item.created_by_uid) && item.created_by_uid > 0)
    || (typeof item.created_by_uid === 'string' && /^[1-9]\d*$/.test(item.created_by_uid));
  const createdAtValid = (typeof item.created_at === 'number'
    && Number.isSafeInteger(item.created_at) && item.created_at > 0)
    || (typeof item.created_at === 'string' && /^[1-9]\d*$/.test(item.created_at));
  if (!projectId || !roomId || !name || !creatorIdValid || !createdAtValid
    || !['canvas', 'workflow', 'agent'].includes(roomType)) return undefined;
  return {
    projectId,
    roomId,
    canvasId: text(item.canvas_id),
    name,
    coverUrl: text(item.cover_url),
    roomType,
  };
}

/** 关联项目由关系接口分页读取；无效记录不展示，原始条数仍用于推进服务端分页。 */
export async function getMyIpProjects(ipId: string, params: MyIpPageParams = {}): Promise<MyIpProjectPage> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpProjectsV3,
    method: 'GET',
    params: { ip_id: normalizeIpId(ipId), ...pagination(params) },
    signal: params.signal,
  }));
  const source = array(data.list);
  const list = source.map(normalizeMyIpProject).filter((item): item is MyIpProject => Boolean(item));
  return {
    total: count(data.total),
    list,
    sourceCount: source.length,
    omittedCount: source.length - list.length,
  };
}

/** 批量绑定选项；即将整页离开时关闭 notify，避免列表刷新 XHR 被导航取消。 */
export interface BindMyIpsToProjectOptions {
  /** 默认 true；为 false 时由调用方在留在当前页时自行 notifyMyIpChanged。 */
  notify?: boolean;
}

/** 使用服务端事务接口批量绑定 IP；整批成功后才广播项目关系变更。 */
export async function bindMyIpsToProject(
  projectId: string,
  ipIds: readonly string[],
  overrideProfile?: MyIpProfile,
  options?: BindMyIpsToProjectOptions,
): Promise<void> {
  if (ipIds.length < 1 || ipIds.length > 100) {
    throw new MyIpRequestError('Invalid IP project binding list');
  }
  // 发请求前完成本地校验和去重，避免损失 uint64 精度。
  const resolvedIpIds = [...new Set(ipIds.map(normalizeIpId))];
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpProjectBindV3,
    method: 'POST',
    data: {
      project_id: requiredString(projectId),
      ip_ids: resolvedIpIds,
      ...(overrideProfile === undefined ? {} : { override_profile: omitMyIpProfileMaterials(record(overrideProfile)) }),
    },
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpProjectBinding],
  }));
  if (data.success !== true) {
    throw new MyIpRequestError('Unexpected IP project binding result');
  }
  if (options?.notify !== false) {
    notifyMyIpChanged();
  }
}

/** 同一创作动作的重试复用幂等键，返回项目和真实房间供调用方导航。 */
export async function createProjectFromMyIp(ipId: string, idempotencyKey: string): Promise<MyIpCreationResult> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpCreateFromV3,
    method: 'POST',
    data: { ip_id: normalizeIpId(ipId), idempotency_key: requiredString(idempotencyKey) },
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpCreation],
  }));
  return {
    projectId: requiredString(data.project_id),
    roomId: requiredString(data.room_id),
    selectedCount: count(data.selected_count),
    existingCount: count(data.existing_count),
    addedCount: count(data.added_count),
    invalid: array(data.invalid).map((value) => {
      const item = record(value);
      return { ipId: normalizeIpId(item.ip_id), reason: text(item.reason) };
    }),
  };
}

export interface MyIpCanvasTarget {
  projectId: string;
  roomId: string;
}

/** 原生快照按项目、画布 ID 保存并校验返回身份，IP 引用同步沿用独立接口。 */
export async function saveMyIpCanvasSnapshot(projectId: string, canvasId: string, snapshot: string, tabId: string): Promise<void> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.saveCanvasDataV3,
    method: 'POST',
    data: {
      project_id: requiredString(projectId),
      canvas_id: requiredString(canvasId),
      tab_id: requiredString(tabId),
      data: requiredString(snapshot),
    },
  }));
  if (data.project_id !== projectId || data.canvas_id !== canvasId || data.project_exist !== true) {
    throw new MyIpRequestError('Unexpected IP canvas save result');
  }
}

/** 画布关联只使用项目和房间身份，节点信息由画布 JSON 持有。 */
function canvasTargetData(target: MyIpCanvasTarget) {
  return {
    project_id: requiredString(target.projectId),
    room_id: requiredString(target.roomId),
  };
}

/** 新增、恢复共用画布级绑定接口；画布关联不改变项目 IP 列表，无需触发列表刷新。 */
export async function applyMyIpToCanvas(target: MyIpCanvasTarget, ipId: string, idempotencyKey: string): Promise<void> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpCanvasApplyV3,
    method: 'POST',
    data: { ...canvasTargetData(target), ip_id: normalizeIpId(ipId), idempotency_key: requiredString(idempotencyKey) },
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpCreation],
  }));
  if (data.project_id !== target.projectId || data.room_id !== target.roomId
    || normalizeIpId(data.ip_id) !== normalizeIpId(ipId)
    || data.project_bound !== true) {
    throw new MyIpRequestError('Unexpected IP canvas synchronization result');
  }
}

/** 最后一个同 IP 快照移除后解除画布关联，保留项目绑定；画布操作不触发项目 IP 列表刷新。 */
export async function removeMyIpFromCanvas(target: MyIpCanvasTarget, ipId: string): Promise<void> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpCanvasRemoveV3,
    method: 'POST',
    data: { ...canvasTargetData(target), ip_id: normalizeIpId(ipId) },
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpCreation],
  }));
  if (data.project_id !== target.projectId || data.room_id !== target.roomId
    || (data.ip_id != null && normalizeIpId(data.ip_id) !== normalizeIpId(ipId))) {
    throw new MyIpRequestError('Unexpected IP canvas removal result');
  }
}

/** 读取有权限的真实房间，校验项目归属并避免混用 workflow 与 agent 路由。 */
export async function getMyIpCreationRoom(result: Pick<MyIpCreationResult, 'roomId' | 'projectId'>): Promise<MyIpProject> {
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.canvasRoomInfoV3,
    method: 'GET',
    params: { room_id: result.roomId },
  }));
  const projectId = typeof data.project_id === 'number' && Number.isSafeInteger(data.project_id)
    ? String(data.project_id) : requiredString(data.project_id);
  if (data.room_id !== result.roomId || projectId !== result.projectId) {
    throw new MyIpRequestError('Unexpected IP creation room');
  }
  return {
    projectId,
    roomId: result.roomId,
    roomType: requiredString(data.room_type),
    canvasId: '',
    name: text(data.name),
    coverUrl: text(data.cover),
  };
}

/** 创建带真实 Agent 房间的草稿；URL 封面使用 cover_url，数字素材 ID 保持兼容字段。 */
export async function createMyIpDraft(input: { sourceSessionId: string; name?: string; summary?: string; coverAssetId?: string; avatarUrl?: string; profile?: MyIpProfile; }): Promise<MyIpItem> {
  const sourceSessionId = validateMyIpRoomId(input.sourceSessionId);
  const item = normalizeMyIp(unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpCreateV3,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: {
      source_session_id: sourceSessionId,
      name: input.name,
      summary: input.summary,
      ...buildMyIpCoverWrite(input.coverAssetId),
      ...(input.avatarUrl === undefined ? {} : { avatar_url: input.avatarUrl === '' ? '' : writableMediaUrl(input.avatarUrl) }),
      profile: input.profile === undefined ? undefined : omitMyIpProfileMaterials(record(input.profile)),
    },
  })));
  if (item.status !== 'draft' || item.assetType !== 'personal' || item.sourceSessionId !== sourceSessionId) {
    throw new MyIpRequestError('Unexpected IP draft or source session');
  }
  return item;
}

/** 创建工作台允许恢复草稿；不放宽原有完成态详情/列表的校验。 */
export async function getMyIpCreationDetail(ipId: string, signal?: AbortSignal): Promise<MyIpItem> {
  const id = normalizeIpId(ipId);
  const item = normalizeMyIp(unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpDetailV3, method: 'GET', params: { ip_id: id }, signal,
  })));
  if (item.ipId !== id || item.assetType !== 'personal' || !['draft', 'complete'].includes(item.status)) {
    throw new MyIpRequestError('Unexpected IP creation detail');
  }
  return item;
}

/** update 可在资料齐全后将草稿标为完成；只接受这个正向状态变化。 */
function isMyIpWriteStatusCompatible(before: MyIpItem, after: MyIpItem): boolean {
  return after.status === before.status || (before.status === 'draft' && after.status === 'complete');
}

/** 等锁或冲突后刷新授权详情；完成态晋级可继续，房间变化与状态回退仍拒绝。 */
async function refreshMyIpWrite(item: MyIpItem, signal?: AbortSignal): Promise<MyIpItem> {
  if (signal?.aborted) throw new MyIpRequestError('IP write context expired');
  const latest = await getMyIpCreationDetail(item.ipId, signal);
  if (signal?.aborted || !isMyIpWriteStatusCompatible(item, latest) || latest.sourceSessionId !== item.sourceSessionId) {
    throw new MyIpRequestError('IP write context changed');
  }
  return latest;
}

/** 旧预发布 update 在缺少 version 时返回通用参数错误；Axios 错误需读取业务信封。 */
function isMyIpUpdateParameterError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const value = error as { status?: number; code?: number | string; response?: { status?: number; data?: { code?: number | string; }; }; };
  const code = value.response?.data?.code ?? value.code;
  const status = value.response?.status ?? value.status;
  return (status === 400 || status === 200) && (code === 11001 || code === '11001');
}

/** 冲突或 update 的旧版本协议错误只刷新重试一次，其他接口不扩大重试范围。 */
async function retryMyIpWrite(
  item: MyIpItem,
  signal: AbortSignal | undefined,
  write: (latest: MyIpItem, includeVersion?: boolean) => Promise<MyIpItem>,
  allowLegacyVersion = false,
): Promise<MyIpItem> {
  if (signal?.aborted) throw new MyIpRequestError('IP write context expired');
  try {
    return await write(item);
  } catch (error) {
    const includeVersion = allowLegacyVersion && isMyIpUpdateParameterError(error);
    if (signal?.aborted || (!includeVersion && !isMyIpVersionConflict(error))) throw error;
    // 不能复用 summary 到达前的旧版本；先合并 Agent 最新写入，再尝试旧协议。
    return write(await refreshMyIpWrite(item, signal), includeVersion);
  }
}

/** 所有主档、素材及完成入口共享同一 IP 的写锁，不同页面实例也不能并发写。 */
function runMyIpWrite(
  item: MyIpItem,
  signal: AbortSignal | undefined,
  write: (latest: MyIpItem, includeVersion?: boolean) => Promise<MyIpItem>,
  allowLegacyVersion = false,
): Promise<MyIpItem> {
  return enqueueMyIpWrite(normalizeIpId(item.ipId), async (waited) => {
    const latest = waited ? await refreshMyIpWrite(item, signal) : item;
    return retryMyIpWrite(latest, signal, write, allowLegacyVersion);
  });
}

/** 主字段冲突按初始详情与本地输入的差异重建，保留远端其他设定。 */
export async function updateMyIp(item: MyIpItem, draft: MyIpEditDraft, locale: string, signal?: AbortSignal, editingBase = item): Promise<MyIpItem> {
  const baseline = getMyIpEditDraft(editingBase);
  const captured = JSON.parse(JSON.stringify(draft)) as MyIpEditDraft;
  return runMyIpWrite(item, signal, (latest, includeVersion) => {
    const rebased = getMyIpEditDraft(latest);
    for (const key of Object.keys(captured) as Array<keyof MyIpEditDraft>) {
      if (JSON.stringify(captured[key]) !== JSON.stringify(baseline[key])) Object.assign(rebased, { [key]: captured[key] });
    }
    return requestMyIpUpdate(latest, rebased, locale, signal, includeVersion);
  }, true);
}

/** 文案与媒体统一走同一个保存队列，媒体省略不会删除现有槽位。 */
export async function saveMyIpDraftSnapshot(item: MyIpItem, snapshot: MyIpDraftSnapshot, signal?: AbortSignal): Promise<MyIpItem> {
  if (item.status !== 'draft') throw new MyIpRequestError('Invalid IP draft snapshot');
  return saveMyIpDraft(item, snapshot, signal);
}

/** 普通保存只走 update；仅更多素材删除按钮传入的明确选择允许先移除资源关系。 */
export function saveMyIpDraft(
  item: MyIpItem,
  snapshot: MyIpDraftSnapshot,
  signal?: AbortSignal,
  onSaved?: (item: MyIpItem, rebased?: boolean) => void,
  deletedMaterialKeys: readonly string[] = [],
): Promise<MyIpItem> {
  const captured = JSON.parse(JSON.stringify(snapshot)) as MyIpDraftSnapshot;
  return enqueueMyIpWrite(normalizeIpId(item.ipId), async (waited) => {
    let current = waited ? await refreshMyIpWrite(item, signal) : item;
    const desired = current === item ? captured : rebaseMyIpSnapshot(item, captured, current);
    // 手动删除之前校验整份变更，普通快照减少资源不代表删除服务端关系。
    validateMyIpDraftSnapshot(current, desired);
    buildMyIpResourceWrites(current, desired.resources);
    const removed = current.resources.filter((resource) => resource.id && isMyIpResourceActive(resource)
      && ['additional_image', 'video'].includes(resource.role)
      && getMyIpMaterialKeys(resource).some((key) => deletedMaterialKeys.includes(key)));
    for (const resource of removed) {
      // eslint-disable-next-line no-await-in-loop
      current = await retryMyIpWrite(current, signal, (latest) => (latest.resources.some((entry) => entry.id === resource.id)
        ? requestMyIpResourceRemoval(latest, resource.id, signal) : Promise.resolve(latest)));
      onSaved?.(current, current !== item);
    }
    const baseline = current;
    const updated = await retryMyIpWrite(current, signal, (latest, includeVersion) => requestMyIpDraftSnapshot(latest, latest === baseline ? desired : rebaseMyIpSnapshot(baseline, desired, latest), signal, includeVersion), true);
    if (signal?.aborted) throw new MyIpRequestError('IP write context expired');
    onSaved?.(updated, current !== item);
    return updated;
  });
}

/** 保留 SDK 兼容入口，但资源保存也统一使用 update，不能调用已下线的写槽接口。 */
export async function saveMyIpDraftResources(initial: MyIpItem, signal?: AbortSignal, onSaved?: (item: MyIpItem, rebased?: boolean) => void): Promise<MyIpItem> {
  return saveMyIpDraft(initial, initial, signal, onSaved);
}

/** 仅供更多素材显式删除使用；三视图和音色的修改统一交给 update。 */
export async function removeMyIpResource(item: MyIpItem, resourceId: string, signal?: AbortSignal): Promise<MyIpItem> {
  return runMyIpWrite(item, signal, async (latest) => {
    const updated = latest !== item && item.resources.some((resource) => resource.id === resourceId)
      && !latest.resources.some((resource) => resource.id === resourceId)
      ? latest : await requestMyIpResourceRemoval(latest, resourceId, signal);
    return updated;
  });
}

/** 完成操作也占用同一写锁；请求只传 ip_id，排在已入队的所有写入之后。 */
export async function completeMyIp(item: MyIpItem, signal?: AbortSignal): Promise<MyIpItem> {
  return runMyIpWrite(item, signal, (latest) => requestMyIpCompletion(latest, signal));
}

/** 删除前先验证整份主档，避免无效文字或封面造成部分成功。 */
function validateMyIpDraftSnapshot(item: MyIpItem, snapshot: MyIpDraftSnapshot): void {
  const profile = preserveMyIpLegacyMedia(item.profile, record(snapshot.profile));
  if (new Blob([JSON.stringify(profile)]).size > 1048576) {
    throw new MyIpRequestError('IP profile exceeds 1 MiB');
  }
  if (item.assetType !== 'personal' || !['draft', 'complete'].includes(item.status) || count(item.version) < 1
    || typeof snapshot.name !== 'string' || typeof snapshot.summary !== 'string'
    || typeof snapshot.coverAssetId !== 'string') {
    throw new MyIpRequestError('Invalid IP draft snapshot');
  }
  if (snapshot.name !== item.name && !snapshot.name.trim()) throw new MyIpRequestError('Missing IP name');
  if (snapshot.coverAssetId && snapshot.coverAssetId !== item.coverAssetId) writableMedia(snapshot.coverAssetId);
  if (snapshot.avatarUrl && snapshot.avatarUrl !== item.avatarUrl) writableMediaUrl(snapshot.avatarUrl);
  if (snapshot.coverWatermarkUrl && snapshot.coverWatermarkUrl !== item.coverWatermarkUrl) {
    writableMediaUrl(snapshot.coverWatermarkUrl);
  }
  if (snapshot.avatarWatermarkUrl && snapshot.avatarWatermarkUrl !== item.avatarWatermarkUrl) {
    writableMediaUrl(snapshot.avatarWatermarkUrl);
  }
}

/** update 默认省略版本，仅旧服务兼容重试携带最新版本；URL 封面走 cover_url。 */
async function requestMyIpDraftSnapshot(
  item: MyIpItem,
  snapshot: MyIpDraftSnapshot,
  signal?: AbortSignal,
  includeVersion = false,
): Promise<MyIpItem> {
  const profile = preserveMyIpLegacyMedia(item.profile, record(snapshot.profile));
  // 保存资源时迁移/替换/删除都以 resources 为准，清掉旧入口防止下次详情复活。
  if (snapshot.resources !== undefined) delete profile.turnaround_visual;
  const resources = buildMyIpResourceWrites(item, snapshot.resources);
  validateMyIpDraftSnapshot(item, snapshot);
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpUpdateV3,
    method: 'POST',
    data: {
      ip_id: normalizeIpId(item.ipId),
      ...(includeVersion ? { version: item.version } : {}),
      // update 用指针语义：省略才是不改，空字符串是清空，空草稿的素材保存不能附带空名称。
      ...(snapshot.name !== item.name ? { name: snapshot.name } : {}),
      ...(snapshot.summary !== item.summary ? { summary: snapshot.summary } : {}),
      ...(snapshot.coverAssetId !== item.coverAssetId ? buildMyIpCoverWrite(snapshot.coverAssetId) : {}),
      ...(snapshot.avatarUrl !== undefined && snapshot.avatarUrl !== item.avatarUrl
        ? { avatar_url: snapshot.avatarUrl === '' ? '' : writableMediaUrl(snapshot.avatarUrl) } : {}),
      // 水印字段与原图分开写入：传了才更新，空串清空；缺省不改，避免覆盖无水印原图。
      ...(snapshot.coverWatermarkUrl !== undefined && snapshot.coverWatermarkUrl !== item.coverWatermarkUrl
        ? { cover_watermark_url: snapshot.coverWatermarkUrl === '' ? '' : writableMediaUrl(snapshot.coverWatermarkUrl) } : {}),
      ...(snapshot.avatarWatermarkUrl !== undefined && snapshot.avatarWatermarkUrl !== item.avatarWatermarkUrl
        ? { avatar_watermark_url: snapshot.avatarWatermarkUrl === '' ? '' : writableMediaUrl(snapshot.avatarWatermarkUrl) } : {}),
      profile,
      ...(resources.length ? { resources } : {}),
    },
    signal,
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpCreation],
  }));
  record(data.profile);
  const updated = normalizeMyIp(data);
  if (updated.ipId !== item.ipId || updated.version <= item.version
    || updated.assetType !== 'personal' || !isMyIpWriteStatusCompatible(item, updated)
    || (updated.sourceSessionId !== undefined && updated.sourceSessionId !== item.sourceSessionId)) {
    throw new MyIpRequestError('Unexpected IP draft snapshot result');
  }
  // 批量新增成功后必须取得真实关系 ID，不能拿本地 URL 冒充 id 继续删除或替换。
  if (resources.length && data.resources == null) return refreshMyIpWrite({ ...updated, sourceSessionId: updated.sourceSessionId ?? item.sourceSessionId }, signal);
  notifyMyIpChanged('content');
  let { coverUrl: resolvedCoverUrl } = item;
  if (snapshot.coverAssetId !== item.coverAssetId) resolvedCoverUrl = getMyIpMediaUrl(snapshot.coverAssetId);
  if (Object.hasOwn(data, 'cover_url')) resolvedCoverUrl = updated.coverUrl;
  return {
    ...updated,
    sourceSessionId: updated.sourceSessionId ?? item.sourceSessionId,
    // update 响应可能省略封面和水印；以本次已提交的快照回填，避免新主图继续显示旧水印。
    coverAssetId: Object.hasOwn(data, 'cover_asset_id') ? updated.coverAssetId : snapshot.coverAssetId,
    coverUrl: resolvedCoverUrl,
    avatarUrl: Object.hasOwn(data, 'avatar_url') ? updated.avatarUrl : item.avatarUrl,
    coverWatermarkUrl: Object.hasOwn(data, 'cover_watermark_url') ? updated.coverWatermarkUrl
      : snapshot.coverWatermarkUrl ?? item.coverWatermarkUrl,
    avatarWatermarkUrl: Object.hasOwn(data, 'avatar_watermark_url') ? updated.avatarWatermarkUrl : item.avatarWatermarkUrl,
    resources: data.resources == null ? item.resources : updated.resources,
  };
}

/** 资源关系 ID 同为 uint64，必须与 IP ID 一起无损输出为 JSON 数字。 */
function serializeMyIpResourceRemoval(value: unknown): string {
  const data = record(value);
  const id = normalizeIpId(data.id);
  return serializeMyIpCreation({ ...data, id }).replace(`"id":"${id}"`, `"id":${id}`);
}

/** 删除的是已授权 IP 的资源关系，保留源素材；后续请求使用返回主档版本。 */
async function requestMyIpResourceRemoval(item: MyIpItem, resourceId: string, signal?: AbortSignal): Promise<MyIpItem> {
  const resource = item.resources.find((entry) => entry.id === resourceId);
  if (item.assetType !== 'personal' || !['draft', 'complete'].includes(item.status) || count(item.version) < 1
    || !resource || !['additional_image', 'video'].includes(resource.role)) {
    throw new MyIpRequestError('Invalid IP resource removal');
  }
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpResourceRemoveV3,
    method: 'POST',
    data: { ip_id: normalizeIpId(item.ipId), id: normalizeIpId(resourceId), version: item.version },
    signal,
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpResourceRemoval],
  }));
  const version = data.ip_version;
  if (data.success !== true || typeof version !== 'number' || !Number.isSafeInteger(version) || version <= item.version) {
    throw new MyIpRequestError('Unexpected IP resource removal result');
  }
  return { ...item, version, resources: item.resources.filter((entry) => entry.id !== resourceId) };
}

/** 封面写入按值分流：未传不改，空串清空，HTTPS 用 cover_url，数字 ID 用兼容字段。 */
function buildMyIpCoverWrite(value?: string): { cover_url?: string; cover_asset_id?: string; } {
  if (value === undefined) return {};
  if (/^[1-9]\d*$/.test(value)) return { cover_asset_id: normalizeIpId(value) };
  return { cover_url: value === '' ? '' : writableMediaUrl(value) };
}

/** 写入只接受素材库数字 ID 或 HTTPS 直链，不再要求先登记资产库。 */
function writableMedia(value: string): string {
  if (/^[1-9]\d*$/.test(value)) return normalizeIpId(value);
  return writableMediaUrl(value);
}

/** resources.url 仅接受 HTTPS 地址，数字资产身份必须走 asset_id。 */
function writableMediaUrl(value: string): string {
  const url = coverUrl(value);
  if (!url || new URL(url).protocol !== 'https:') throw new MyIpRequestError('IP resource requires an HTTPS URL');
  return url;
}

/** 全量本地槽位转换成增量 resources；单槽替换复用关系 ID，未变化资源不重写。 */
function buildMyIpResourceWrites(item: MyIpItem, desired?: MyIpResource[]): Record<string, unknown>[] {
  if (!desired) return [];
  const active = desired.filter(isMyIpResourceActive);
  for (const [role, type] of Object.entries(MY_IP_RESOURCE_TYPES)) {
    const slots = active.filter((entry) => entry.role === role);
    if (slots.length > (role === 'additional_image' ? MY_IP_MAX_MATERIALS : 1)
      || slots.some((entry) => entry.type !== type)) {
      throw new MyIpRequestError('Invalid IP resource slot count or type');
    }
  }
  return active.flatMap((resource) => {
    if (!MY_IP_RESOURCE_TYPES[resource.role]) {
      if (item.resources.some((entry) => JSON.stringify(entry) === JSON.stringify(resource))) return [];
      throw new MyIpRequestError('Invalid IP resource role');
    }
    const existing = item.resources.find((entry) => isMyIpResourceActive(entry) && entry.role === resource.role
      && (resource.id ? entry.id === resource.id : getMyIpResourceKey(entry) === getMyIpResourceKey(resource)
        || resource.role !== 'additional_image'));
    if (resource.id && !existing) throw new MyIpRequestError('Unknown IP resource relation');
    const sortOrder = count(resource.sortOrder);
    if (existing && existing.id && !getMyIpMediaUrl(existing.assetId) && getMyIpResourceKey(existing) === getMyIpResourceKey(resource)
      && (!resource.assetId || existing.assetId === resource.assetId)
      && existing.type === resource.type && existing.sortOrder === sortOrder
      && (resource.name === undefined || resource.name === existing.name)
      && (resource.voiceId === undefined || resource.voiceId === existing.voiceId)
      && (resource.watermarkUrl === undefined || resource.watermarkUrl === existing.watermarkUrl)) return [];
    const legacyUrl = getMyIpMediaUrl(resource.assetId);
    const asset = resource.assetId && !legacyUrl
      ? { asset_id: normalizeIpId(resource.assetId), ...(resource.url ? { url: writableMediaUrl(resource.url) } : {}) }
      : { url: writableMediaUrl(resource.url || legacyUrl || '') };
    if (resource.voiceId !== undefined && typeof resource.voiceId !== 'string') throw new MyIpRequestError('Invalid IP voice identity');
    // 有水印地址时与原图一并写入；空串表示清空水印，不删除原图 url。
    const watermark = resource.watermarkUrl === undefined ? {}
      : { watermark_url: resource.watermarkUrl === '' ? '' : writableMediaUrl(resource.watermarkUrl) };
    return [{
      ...(existing?.id ? { id: normalizeIpId(existing.id) } : {}),
      ...asset,
      ...watermark,
      resource_role: resource.role,
      resource_type: resource.type,
      sort_order: sortOrder,
      ...(resource.name === undefined ? {} : { name: resource.name }),
      ...(resource.voiceId === undefined ? {} : { voice_id: resource.voiceId }),
    }];
  });
}

/** 以已保存主档决定是否 complete；update 已返回完成态时直接沿用，避免重复完成。 */
async function requestMyIpCompletion(draft: MyIpItem, signal?: AbortSignal): Promise<MyIpItem> {
  if (draft.status === 'complete' && draft.assetType === 'personal' && count(draft.version) >= 1) return draft;
  const setting = draft.profile.character_setting;
  const personality = setting?.personality;
  // 按产品字段表校验已落库角色，三视图和旧 summary/persona/language_style/visual_style 不再拦截完成。
  if (draft.status !== 'draft' || draft.assetType !== 'personal' || count(draft.version) < 1
    || [draft.name, draft.coverAssetId, setting?.identity, setting?.appearance]
      .some((value) => typeof value !== 'string' || !value.trim())
    || !Array.isArray(personality) || !personality.some((value) => typeof value === 'string' && value.trim())) {
    throw new MyIpRequestError('IP completion fields are missing');
  }
  const id = normalizeIpId(draft.ipId);
  const item = normalizeMyIp(unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpCompleteV3,
    method: 'POST',
    // complete 的独立参数表只声明 ip_id，版本已由前序写队列校验和推进。
    data: { ip_id: id },
    signal,
    headers: { 'Content-Type': 'application/json' },
    transformRequest: [serializeMyIpCreation],
  })));
  if (item.ipId !== id || item.status !== 'complete' || item.assetType !== 'personal' || item.version < draft.version) throw new MyIpRequestError('IP completion failed');
  notifyMyIpChanged('content');
  return item;
}

/** 角色卡数据与模型生成分开，返回完整可访问设定供实际渲染/生成消费。 */
export async function getMyIpRoleCardData(ipId: string, signal?: AbortSignal): Promise<MyIpItem> {
  const id = normalizeIpId(ipId);
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpRoleCardDataV3, method: 'GET', params: { ip_id: id }, signal,
  }));
  const item = normalizeMyIp(data.ip);
  if (item.ipId !== id || data.render_mode !== 'role_card') throw new MyIpRequestError('Invalid IP role card data');
  return item;
}

/**
 * 角色卡异步任务保存核对结果。
 * saved 只表示本仓是否已有该任务凭据，不能解释为生成失败。
 */
export interface MyIpRoleCardTaskResult {
  ipId: string;
  taskId: string;
  currentTaskId: string;
  saved: boolean;
  isCurrent: boolean;
  versionId: string;
  versionNo: number;
  roleCardUrl?: string;
  savedAt: number;
  version: number;
}

/** 将角色卡任务 detail/bind 响应规范为前端可消费结构。 */
function normalizeMyIpRoleCardTaskResult(value: unknown, expectedIpId: string): MyIpRoleCardTaskResult {
  const data = record(value);
  const ipId = normalizeIpId(data.ip_id);
  if (ipId !== expectedIpId) throw new MyIpRequestError('Unexpected IP role card task result');
  if (typeof data.saved !== 'boolean' || typeof data.is_current !== 'boolean') {
    throw new MyIpRequestError('Invalid IP role card task flags');
  }
  // version_id 未保存时为 0；normalizeIpId 只接受正整数，这里单独放行零值。
  const versionId = data.version_id == null || data.version_id === '' || data.version_id === 0 || data.version_id === '0'
    ? '0'
    : normalizeIpId(data.version_id);
  return {
    ipId,
    taskId: text(data.task_id),
    currentTaskId: text(data.current_task_id),
    saved: data.saved,
    isCurrent: data.is_current,
    versionId,
    versionNo: count(data.version_no ?? 0),
    ...(coverUrl(data.role_card_url) ? { roleCardUrl: coverUrl(data.role_card_url) } : {}),
    savedAt: count(data.saved_at ?? 0),
    version: count(data.version),
  };
}

/**
 * 查询角色卡任务是否已保存凭据。
 * 不传 taskId 时返回最近绑定任务；生成中/失败仍需另行查询执行状态。
 *
 * @param {string} ipId
 * @param {string} [taskId]
 * @param {AbortSignal} [signal]
 * @returns {Promise<MyIpRoleCardTaskResult>}
 */
export async function getMyIpRoleCardTaskDetail(
  ipId: string,
  taskId?: string,
  signal?: AbortSignal,
): Promise<MyIpRoleCardTaskResult> {
  const id = normalizeIpId(ipId);
  const trimmedTaskId = taskId?.trim();
  const data = unwrap(await canvasRequest.request({
    url: APP.roboneo.api.myIpRoleCardTaskDetailV3,
    method: 'GET',
    params: {
      ip_id: id,
      ...(trimmedTaskId ? { task_id: trimmedTaskId } : {}),
    },
    signal,
  }));
  return normalizeMyIpRoleCardTaskResult(data, id);
}

/**
 * 绑定角色卡生成任务，供离开后按 task_id 核对保存结果。
 * 绑定不改当前图片；CAS 冲突时用最新 version 重试一次。
 *
 * @param {string} ipId
 * @param {string} taskId
 * @param {number} version
 * @param {AbortSignal} [signal]
 * @returns {Promise<MyIpRoleCardTaskResult>}
 */
export async function bindMyIpRoleCardTask(
  ipId: string,
  taskId: string,
  version: number,
  signal?: AbortSignal,
): Promise<MyIpRoleCardTaskResult> {
  const id = normalizeIpId(ipId);
  const trimmedTaskId = taskId.trim();
  if (!trimmedTaskId || trimmedTaskId.length > 128) {
    throw new MyIpRequestError('Invalid IP role card task id');
  }
  if (!Number.isSafeInteger(version) || version < 1) {
    throw new MyIpRequestError('Invalid IP role card bind version');
  }

  /** @param {number} nextVersion 绑定使用的 IP CAS 版本 */
  const requestBind = async (nextVersion: number): Promise<MyIpRoleCardTaskResult> => {
    const data = unwrap(await canvasRequest.request({
      url: APP.roboneo.api.myIpRoleCardTaskBindV3,
      method: 'POST',
      data: { ip_id: id, task_id: trimmedTaskId, version: nextVersion },
      signal,
      headers: { 'Content-Type': 'application/json' },
      transformRequest: [serializeMyIpCreation],
    }));
    return normalizeMyIpRoleCardTaskResult(data, id);
  };

  try {
    return await requestBind(version);
  } catch (error) {
    if (!isMyIpVersionConflict(error) || signal?.aborted) throw error;
    // 绑定前 version 过期时读最新主档再绑一次，避免刚编辑过的 IP 首次绑定必失败。
    const latest = await getMyIpRoleCardData(id, signal);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (latest.version < 1) throw error;
    return requestBind(latest.version);
  }
}
